import { useState, useEffect } from 'react';
import { CROPS } from '../data/crops';
import { cropSuitabilityScale, attainableYieldScale, getSuitabilityLevel } from '../data/suitabilityScales';
import { useAppContext } from '../store/AppContext';

export interface GeneralSuitability {
  crop: string | null;
  average: string | null;
  mode: string | null;
  yieldAttainable: string | null;
  yieldRelative: string | null;
}

export interface CropSuitabilityResult {
  cropCode: string;
  cropName: string;
  cropSuitability: number;
  averageSuitability: number;
  modeSuitability: number;
  attainableYield: number;
  relativeYield: number;
  suitability: string | null;
  average: string | null;
  mode: string | null;
  yieldAttainable: string | null;
  yieldRelative: string | null;
}

export const useCropSuitability = () => {
  const { state } = useAppContext();
  const { positionnew, bufferRadius, soilCode, cropCode, waterCode, inputCode } = state;
  const crops = CROPS;
  const [cropSuitability, setCropSuitability] = useState<number | null>(null);
  const [averageSuitability, setAverageSuitability] = useState<number | null>(null);
  const [modeSuitability, setModeSuitability] = useState<number | null>(null);
  const [attainableYield, setAttainableYield] = useState<number | null>(null);
  const [relativeYield, setRelativeYield] = useState<number | null>(null);
  const [dataError, setDataError] = useState<boolean>(true);
  const [allCropDataError, setAllCropDataError] = useState<boolean>(true);
  const [generalSuitability, setGeneralSuitability] = useState<GeneralSuitability>({
    crop: null, average: null, mode: null, yieldAttainable: null, yieldRelative: null,
  });
  const [allCropSuitability, setAllCropSuitability] = useState<CropSuitabilityResult[]>([]);
  // loading covers the main and alternate crop fetches; fetchError holds the main fetch's error message
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  // Increments on every fetch run, so callers can restart timers even while still loading
  const [attempt, setAttempt] = useState(0);
  const retry = () => setRetryCount((c) => c + 1);

  const buildURL = (code: string, type: 'SIS' | 'YLS') =>
    `https://storage.googleapis.com/fao-gismgr-soilfer-data/DATA/SOILFER/MAPSET/${type}/SOILFER.${type}.${code}.${soilCode}S.${inputCode + waterCode}.tif`;

  const clipURL = (url1: string, url2: string) =>
    `https://data.apps.fao.org/soilfer/cropsuit/clip/clip?action=cliptiff&url=${encodeURIComponent(url1)}&url=${encodeURIComponent(url2)}&latitude=${positionnew.lat}&longitude=${positionnew.lng}&buffer=${bufferRadius / 1000}`;

  const fetchOneCrop = async (cropName: string, code: string): Promise<CropSuitabilityResult> => {
    try {
      const url = clipURL(buildURL(code, 'SIS'), buildURL(code, 'YLS'));
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const s = await res.json();

      let si = s.tiff0.pixel_value !== 'error' ? s.tiff0.pixel_value : null;
      let avg = s.tiff0.buffer_avg !== 'error' ? Number(s.tiff0.buffer_avg.toFixed(0)) : null;
      let mode = s.tiff0.buffer_mode !== 'error' ? s.tiff0.buffer_mode : null;
      let ay = s.tiff1.pixel_value !== 'error' ? s.tiff1.pixel_value : null;
      let ry = ay !== null ? (ay / crops[cropName].maxYield) * 10000 : null;

      if (si === null) { si = 11; avg = 11; mode = 11; ay = 0; ry = 0; }
      if (ay === null) { ay = 0; ry = 0; }

      return {
        cropCode: code, cropName,
        cropSuitability: si, averageSuitability: avg, modeSuitability: mode,
        attainableYield: ay, relativeYield: ry,
        suitability: getSuitabilityLevel(si, cropSuitabilityScale),
        average: getSuitabilityLevel(avg, cropSuitabilityScale),
        mode: getSuitabilityLevel(mode, cropSuitabilityScale),
        yieldAttainable: getSuitabilityLevel(ay, attainableYieldScale),
        yieldRelative: getSuitabilityLevel(ay, attainableYieldScale),
      };
    } catch {
      return {
        cropCode: code, cropName,
        cropSuitability: 11, averageSuitability: 11, modeSuitability: 11,
        attainableYield: 0, relativeYield: 0,
        suitability: getSuitabilityLevel(0, cropSuitabilityScale),
        average: getSuitabilityLevel(0, cropSuitabilityScale),
        mode: getSuitabilityLevel(0, cropSuitabilityScale),
        yieldAttainable: getSuitabilityLevel(0, attainableYieldScale),
        yieldRelative: getSuitabilityLevel(0, attainableYieldScale),
      };
    }
  };

  useEffect(() => {
    if (!soilCode || !cropCode || !waterCode || !inputCode || !positionnew) return;

    const cropName = Object.keys(crops).find(k => crops[k].SoilFERCode === cropCode);
    // Ignore results from runs superseded by a retry or a parameter change
    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    setAttempt((a) => a + 1);

    const fetchMain = async () => {
      try {
        const url = clipURL(buildURL(cropCode, 'SIS'), buildURL(cropCode, 'YLS'));
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const s = await res.json();
        if (cancelled) return;

        let si = s.tiff0.pixel_value !== 'error' ? s.tiff0.pixel_value : null;
        let avg = s.tiff0.buffer_avg !== 'error' ? Number(s.tiff0.buffer_avg.toFixed(0)) : null;
        let mode = s.tiff0.buffer_mode !== 'error' ? s.tiff0.buffer_mode : null;
        let ay = s.tiff1.pixel_value !== 'error' ? s.tiff1.pixel_value : null;
        let ry = (ay !== null && cropName) ? (ay / crops[cropName].maxYield) * 10000 : null;

        if (si === null) { si = 11; avg = 11; mode = 11; ay = 0; ry = 0; }
        if (ay === null) { ay = 0; ry = 0; }

        setCropSuitability(si);
        setAverageSuitability(avg);
        setModeSuitability(mode);
        setAttainableYield(ay);
        setRelativeYield(ry);
        setDataError(false);
        setGeneralSuitability({
          crop: getSuitabilityLevel(si, cropSuitabilityScale),
          average: getSuitabilityLevel(avg, cropSuitabilityScale),
          mode: getSuitabilityLevel(mode, cropSuitabilityScale),
          yieldAttainable: getSuitabilityLevel(ay, attainableYieldScale),
          yieldRelative: getSuitabilityLevel(ay, attainableYieldScale),
        });
      } catch (error) {
        if (cancelled) return;
        // Values stay null so the loading view shows while the user decides whether to retry
        setDataError(true);
        setCropSuitability(null); setAverageSuitability(null); setModeSuitability(null);
        setAttainableYield(null); setRelativeYield(null);
        setGeneralSuitability({ crop: 'Unknown', average: 'Unknown', mode: 'Unknown', yieldAttainable: 'Unknown', yieldRelative: 'Unknown' });
        setFetchError(error instanceof Error ? error.message : 'Unknown error');
        setLoading(false);
      }
    };

    const fetchAll = async () => {
      const results = await Promise.all(
        Object.entries(crops).map(([name, info]) => fetchOneCrop(name, info.SoilFERCode))
      );
      if (cancelled) return;
      setAllCropSuitability(results);
      setAllCropDataError(false);
    };

    Promise.all([fetchMain(), fetchAll()]).then(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [positionnew, bufferRadius, soilCode, cropCode, waterCode, inputCode, retryCount]);

  return {
    cropSuitability, averageSuitability, modeSuitability, attainableYield, relativeYield,
    dataError, allCropDataError, generalSuitability, allCropSuitability,
    loading, fetchError, attempt, retry,
  };
};
